/**
 * Ratify a Plan of Record as an official record (migration 648).
 *
 * Ratification is not a status change — it freezes content, writes the
 * immutable versioned snapshot, and records who ratified under what authority.
 * Both fields are required by the ratify_por RPC; this modal collects them.
 */

import { useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { ratifyPor } from '../../lib/planOfRecordService';
import { GhostButton, PorModal, PrimaryButton } from './porUi';
import { RATIFY_POR_LABEL, RATIFYING_POR_LABEL } from '../../lib/planning/vocabulary';

export function RatifyPorModal({
  porId,
  onClose,
  onRatified,
}: {
  porId: string;
  onClose: () => void;
  onRatified: (version: number) => Promise<void> | void;
}) {
  const [authority, setAuthority] = useState('');
  const [rationale, setRationale] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const valid = authority.trim().length > 0 && rationale.trim().length > 0;

  const handleConfirm = async () => {
    if (!valid) {
      setError('Ratification authority and rationale are both required.');
      return;
    }
    setBusy(true);
    setError(null);
    const { data, error: err } = await ratifyPor(porId, {
      authority: authority.trim(),
      rationale: rationale.trim(),
    });
    if (err || !data) {
      setError(err ?? 'Failed to ratify.');
      setBusy(false);
      return;
    }
    await onRatified(data.version);
  };

  return (
    <PorModal
      title="Ratify as the record"
      subtitle="Content freezes and an immutable versioned snapshot is written. To amend later, reopen for amendment and ratify again as the next version."
      onClose={onClose}
      footer={
        <>
          <GhostButton onClick={onClose} disabled={busy}>
            Cancel
          </GhostButton>
          <PrimaryButton onClick={handleConfirm} disabled={busy || !valid}>
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5" />
              {busy ? RATIFYING_POR_LABEL : RATIFY_POR_LABEL}
            </span>
          </PrimaryButton>
        </>
      }
    >
      <label className="block text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1.5">
        Ratification authority <span className="text-luminous-coral-700">*</span>
      </label>
      <input
        autoFocus
        value={authority}
        onChange={(e) => {
          setAuthority(e.target.value);
          if (error) setError(null);
        }}
        placeholder='The role under which you act — e.g. "CEO", "VP Product"'
        className="w-full border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] px-3 py-2 text-sm mb-3"
      />
      <label className="block text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1.5">
        Rationale <span className="text-luminous-coral-700">*</span>
      </label>
      <textarea
        rows={3}
        value={rationale}
        onChange={(e) => {
          setRationale(e.target.value);
          if (error) setError(null);
        }}
        placeholder="Why this plan is being ratified now…"
        className="w-full border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] px-3 py-2 text-sm"
      />
      {error && <p className="mt-2 text-sm text-luminous-coral-700">{error}</p>}
    </PorModal>
  );
}
